import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Button,
  FormControl,
  FormLabel,
  Icon,
  IconList,
  Input,
  Message,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'components/core';

import { Inline, Stack } from 'components/layout';
import { EMAIL_REGEX } from 'util/validateEmail';

export type AddEmailModalProps = {
  onClose: () => void;
  onSubmit: (email: string) => Promise<void>;

  error: 'taken' | 'email_claimed_by_another_user' | null;
  isOpen: boolean;
};

export const CloseButton = (props: { onClose?: () => any }) => {
  return (
    <Button
      iconOnly
      type="neutral"
      variant="ghost"
      size="sm"
      onClick={() => props.onClose?.()}
      aria-label="Close"
    >
      <Icon icon={IconList.light.faClose} size="lg" />
    </Button>
  );
};

export const AddEmailModal = (props: AddEmailModalProps) => {
  const {
    register,
    formState: { errors },
    getValues,
    reset,
    trigger,
  } = useForm<{ email: string }>({ mode: 'onBlur' });

  const { onClose, isOpen, onSubmit, error } = props;

  const emailValidationPattern = {
    value: EMAIL_REGEX,
    message: 'Please enter a valid email address in the format: you@domain.com',
  };

  const [loading, setLoading] = useState(false);

  const errorMessage = {
    taken: 'Email has already been added to your account',
    email_claimed_by_another_user: 'Email has been claimed by another user',
  };

  return (
    <Modal isOpen={isOpen}>
      <Inline alignX="spaceBetween" alignY="baseline">
        <ModalHeader>Add new email</ModalHeader>
        <CloseButton onClose={onClose} />
      </Inline>
      <ModalBody>
        <Stack spacing={6}>
          Add a new email address to your account. Please follow the
          instructions in the verification email that will be sent to this
          address
          <div>
            <FormControl error={errors.email?.message}>
              <FormLabel size="sm">Email</FormLabel>
              <Input
                size="sm"
                inputType="email"
                placeholder="you@domain.com"
                {...register('email', {
                  required: 'Please enter your email address',
                  pattern: emailValidationPattern,
                })}
                autoComplete="email"
              />
            </FormControl>
          </div>
          {error && (
            <Message variant="prominent" type="danger">
              {errorMessage[error]}
            </Message>
          )}
        </Stack>
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={() => {
            reset();
            onClose();
          }}
          disabled={loading}
          variant="outline"
        >
          Cancel
        </Button>
        <Button
          type={'brand'}
          onClick={async () => {
            const valid = await trigger();

            if (!valid) return;

            let isRedirecting = false;
            try {
              setLoading(true);
              await onSubmit(getValues('email'));
              reset({ email: '' });
            } catch (error: any) {
              // If redirecting to SSO, keep loading state and don't reset email
              if (error?.redirect) {
                isRedirecting = true;
                return;
              }
              setLoading(false);
            } finally {
              if (!isRedirecting) setLoading(false);
            }
          }}
          isLoading={loading}
        >
          Add email
        </Button>
      </ModalFooter>
    </Modal>
  );
};
